// ---------------------------------------------------------------------------
// Analytics — a thin, safe wrapper over the Google Analytics 4 tag that is
// already loaded in public/index.html (G-8ZFZ4EPD0L).
//
// Why gtag and not the Firebase Analytics SDK: the iOS app is a native shell
// around soulemap.com, so the page's own tag already covers both surfaces —
// and adding a second tracker to the same GA4 property would double-count
// every visit. Instead we mark each session with a `platform` property so
// app traffic and web traffic can be told apart in every report.
//
// Everything here is defensive: if the tag is blocked (ad blockers, private
// modes) every call is a silent no-op. Analytics must never break the site.
// ---------------------------------------------------------------------------

import { isInNativeApp } from './platform';

const hasGtag = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/** Record an event. Never throws. */
export function track(name, params = {}) {
  try {
    if (!hasGtag()) return;
    window.gtag('event', name, params);
  } catch (e) {
    /* analytics must never break the page */
  }
}

/** Tell GA4 which surface this session is on, and who (anonymously) it is. */
export function identify(uid) {
  try {
    if (!hasGtag()) return;
    window.gtag('set', 'user_properties', {
      platform: isInNativeApp() ? 'ios_app' : 'web',
      signed_in: uid ? 'yes' : 'no',
    });
    // send_page_view:false — index.html's config already sent the first
    // view; without this every sign-in would inflate the page count.
    if (uid) window.gtag('config', 'G-8ZFZ4EPD0L', { user_id: uid, send_page_view: false });
  } catch (e) { /* no-op */ }
}

/** A screen view for single-page navigation (GA4 sees no page reload). */
export function trackPageView(path, title) {
  try {
    if (!hasGtag()) return;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
      platform: isInNativeApp() ? 'ios_app' : 'web',
    });
  } catch (e) { /* no-op */ }
}

// ── Named events ───────────────────────────────────────────────────────────
// One place to see everything SouleMap measures. Names follow GA4's
// recommended vocabulary where one exists, so the standard reports light up.

export const events = {
  signUp: (method = 'email') => track('sign_up', { method }),
  login: (method = 'email') => track('login', { method }),

  memorialCreated: (kind) => track('memorial_created', { memorial_kind: kind }),   // 'quick' | 'full' | 'forever'
  memorialEdited: () => track('memorial_edited'),
  starClaimed: (region) => track('star_claimed', { star_region: region || 'unknown' }),

  // Engagement — the signals that a memorial is alive
  memoryPosted: () => track('memory_posted'),
  flowerLeft: () => track('flower_left'),
  cardSent: (paid) => track('card_sent', { paid: paid ? 'yes' : 'no' }),
  candleLit: () => track('candle_lit'),
  galleryOpened: (photoCount) => track('gallery_opened', { photo_count: photoCount || 0 }),
  memorialShared: (method) => track('share', { method: method || 'link', content_type: 'memorial' }),

  // Revenue funnel — 'begin_checkout' is a GA4 standard event, so these
  // appear in the built-in monetisation reports.
  beginCheckout: (item, value, store) => track('begin_checkout', {
    currency: 'USD',
    value: value || 0,
    items: [{ item_name: item }],
    payment_store: store || 'stripe',       // 'stripe' | 'apple'
  }),
  purchased: (item, value, store) => track('purchase', {
    currency: 'USD',
    value: value || 0,
    items: [{ item_name: item }],
    payment_store: store || 'stripe',
  }),

  contentReported: () => track('content_reported'),
};

// ── Site health ────────────────────────────────────────────────────────────

/**
 * Core Web Vitals → GA4. These were already being measured by
 * reportWebVitals and thrown away; now they land in a report where slow
 * pages become visible (LCP = how long until the page looks ready,
 * CLS = how much it jumps around, FID = how soon it responds to a tap).
 */
export function sendWebVital({ name, delta, value, id }) {
  track('web_vitals', {
    metric_name: name,
    // GA4 metrics must be integers; CLS is a small decimal, so scale it.
    metric_value: Math.round(name === 'CLS' ? (value || delta) * 1000 : (value || delta)),
    metric_id: id,
    platform: isInNativeApp() ? 'ios_app' : 'web',
  });
}

/** Uncaught errors and rejected promises, so real-world breakage is visible. */
function installErrorTracking() {
  if (typeof window === 'undefined' || window.__smErrorsHooked) return;
  window.__smErrorsHooked = true;

  window.addEventListener('error', (e) => {
    track('exception', {
      description: `${e.message || 'error'} @ ${(e.filename || '').split('/').pop()}:${e.lineno || 0}`,
      fatal: false,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const msg = (reason && (reason.message || reason.code)) || String(reason || 'unknown');
    track('exception', { description: `unhandled: ${String(msg).slice(0, 140)}`, fatal: false });
  });
}

/** Call once at app start. */
export function initAnalytics() {
  identify(null);
  installErrorTracking();
}
